import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { 
  HardDrive, 
  Search, 
  Shield, 
  Code,
  CheckCircle,
  AlertTriangle,
  Database,
  Smartphone,
  Computer,
  Flame
} from 'lucide-react';

const DigitalForensicsPage = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const services = [
    {
      title: "Digital Evidence Recovery",
      description: "Advanced digital forensics techniques to recover, preserve, and analyze digital evidence from various electronic devices and storage media.",
      icon: HardDrive,
      image: "https://images.unsplash.com/photo-1488590528505-98d2b5aba04b",
      features: [
        "Hard drive and SSD recovery",
        "Deleted file reconstruction",
        "Damaged media restoration",
        "Encrypted data recovery",
        "Cloud storage forensics",
        "Network traffic analysis"
      ]
    },
    {
      title: "Computer Forensics Investigation",
      description: "Comprehensive computer system analysis for legal proceedings, incident response, and security breach investigations.",
      icon: Computer,
      image: "https://images.pexels.com/photos/5475750/pexels-photo-5475750.jpeg",
      features: [
        "System imaging and preservation",
        "Timeline reconstruction",
        "User activity analysis",
        "Malware detection and analysis",
        "Registry and log file examination",
        "Court-admissible reporting"
      ]
    },
    {
      title: "Mobile Device Forensics",
      description: "Specialized mobile forensics for smartphones, tablets, and IoT devices to extract and analyze digital evidence.",
      icon: Smartphone,
      image: "https://images.pexels.com/photos/8728559/pexels-photo-8728559.jpeg",
      features: [
        "iOS and Android extraction",
        "Call logs and message recovery",
        "Location data analysis",
        "App data reconstruction",
        "Deleted media recovery",
        "SIM card forensics"
      ]
    },
    {
      title: "Custom Software Development",
      description: "Specialized software solutions for government agencies, including forensic tools, security applications, and data analysis platforms.",
      icon: Code,
      image: "https://images.pexels.com/photos/7688543/pexels-photo-7688543.jpeg",
      features: [
        "Forensic analysis tools",
        "Security assessment software",
        "Data visualization platforms",
        "Automated reporting systems",
        "Integration with existing systems",
        "Government compliance ready"
      ]
    }
  ];

  const forensicsCapabilities = [
    {
      title: "Advanced Recovery Tools",
      description: "State-of-the-art hardware and software for data recovery and analysis",
      icon: Database
    },
    {
      title: "Chain of Custody",
      description: "Strict evidence handling protocols for legal admissibility",
      icon: Shield
    },
    {
      title: "Expert Testimony",
      description: "Certified forensic experts available for court proceedings",
      icon: Search
    },
    {
      title: "Emergency Response",
      description: "24/7 incident response for critical data recovery situations",
      icon: AlertTriangle
    }
  ];

  const recoveryTypes = [
    "Government Investigations",
    "Corporate Incident Response",
    "Legal Discovery Support",
    "Insurance Fraud Cases",
    "Criminal Investigations", 
    "Intellectual Property Theft",
    "Cyber Attack Analysis",
    "Employee Misconduct Cases"
  ];

  const technicalSpecs = [
    {
      category: "Forensic Tools",
      specs: [
        "EnCase Forensic Suite",
        "Cellebrite Mobile Forensics",
        "X-Ways Forensics",
        "Oxygen Forensic Suite",
        "MSAB XRY Mobile",
        "Custom CIARL Tools"
      ]
    },
    {
      category: "Data Recovery",
      specs: [
        "Hardware write blockers",
        "Clean room facilities",
        "Specialized recovery hardware",
        "Advanced imaging techniques",
        "Damaged media specialists",
        "Secure evidence storage"
      ]
    },
    {
      category: "Analysis & Reporting",
      specs: [
        "Timeline analysis software",
        "Data visualization tools",
        "Automated reporting systems",
        "Expert witness testimony",
        "Legal documentation",
        "Chain of custody protocols"
      ]
    }
  ];

  const emergencyEquipment = [
    {
      name: "Firefighting Equipment",
      description: "Emergency response equipment for fire suppression and safety"
    },
    {
      name: "Data Recovery Kits",
      description: "Portable forensics kits for field data recovery operations"
    },
    {
      name: "Emergency Communications",
      description: "Secure communication systems for emergency operations"
    },
    {
      name: "Backup Power Systems",
      description: "Uninterruptible power supplies and backup generators"
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section 
        className="section-padding relative overflow-hidden"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1488590528505-98d2b5aba04b')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-yellow-900/90 to-near-black/90 z-0" />
        <div className="absolute inset-0 grid-pattern opacity-20 z-1" />
        
        <div className="container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-yellow-700 rounded-16 flex items-center justify-center">
                <HardDrive className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="heading-font text-4xl md:text-6xl font-bold text-off-white">
                  Digital Forensics & <span className="text-royal-gold">Recovery</span>
                </h1>
              </div>
            </div>
            
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed max-w-3xl">
              Expert digital forensics, data recovery solutions, custom software development, 
              and emergency equipment services. Supporting law enforcement, government agencies, 
              and corporate investigations with court-admissible evidence and expert analysis.
            </p>
            

          </motion.div>
        </div>
      </section>

      {/* Services Detail */}
      <section className="section-padding bg-gray-dark" ref={ref}>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="heading-font text-4xl md:text-5xl font-bold mb-6 text-off-white">
              Digital Forensics <span className="text-royal-gold">Services</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Advanced digital investigation and data recovery services with court-admissible 
              evidence collection and expert testimony for government and corporate clients.
            </p>
          </motion.div>

          <div className="space-y-20">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
                }`}
              >
                {/* Content */}
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-yellow-700 rounded-16 flex items-center justify-center">
                      <service.icon className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h3 className="heading-font text-3xl md:text-4xl font-bold text-off-white">
                        {service.title}
                      </h3>
                    </div>
                  </div>
                  
                  <p className="text-lg text-gray-300 mb-8 leading-relaxed">
                    {service.description}
                  </p>
                  
                  {/* Features List */}
                  <div className="space-y-3">
                    {service.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center space-x-3">
                        <CheckCircle className="w-5 h-5 text-royal-gold flex-shrink-0" />
                        <span className="text-gray-200">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Image */}
                <div className={index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}>
                  <div className="relative rounded-24 overflow-hidden group">
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="w-full h-80 object-cover transition-transform duration-300 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-near-black/60 to-transparent" />
                    <div className="absolute bottom-6 left-6">
                      <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-yellow-700 rounded-12 flex items-center justify-center">
                        <service.icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Forensics Capabilities */}
      <section className="section-padding bg-near-black">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="heading-font text-4xl md:text-5xl font-bold mb-6 text-off-white">
              Forensics <span className="text-royal-gold">Capabilities</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {forensicsCapabilities.map((capability, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="glass-morphism p-6 rounded-16 text-center hover:border-royal-gold transition-all duration-300 group"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-royal-gold to-gold-light rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <capability.icon className="w-8 h-8 text-near-black" />
                </div>
                <h3 className="text-lg font-semibold text-off-white mb-2 group-hover:text-royal-gold transition-colors duration-300">
                  {capability.title}
                </h3>
                <p className="text-gray-300 text-sm">
                  {capability.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Specifications */}
      <section className="section-padding bg-gray-dark">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="heading-font text-4xl md:text-5xl font-bold mb-6 text-off-white">
              Technical <span className="text-royal-gold">Capabilities</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Industry-leading forensic tools and methodologies with strict adherence 
              to legal standards and chain of custody requirements.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {technicalSpecs.map((spec, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="service-card"
              >
                <h3 className="heading-font text-2xl font-semibold text-royal-gold mb-6">
                  {spec.category}
                </h3>
                <div className="space-y-3">
                  {spec.specs.map((item, itemIndex) => (
                    <div key={itemIndex} className="flex items-center space-x-3">
                      <CheckCircle className="w-4 h-4 text-royal-gold flex-shrink-0" />
                      <span className="text-gray-200 text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Emergency Equipment */}
      {/* <section className="section-padding bg-near-black">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              <h2 className="heading-font text-4xl md:text-5xl font-bold mb-6 text-off-white">
                Emergency <span className="text-royal-gold">Equipment</span>
              </h2>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                In addition to digital forensics, we provide emergency response equipment 
                and support services for critical situations and disaster recovery operations.
              </p>
              
              <div className="space-y-4 mb-8">
                {emergencyEquipment.map((equipment, index) => (
                  <div key={index} className="glass-morphism p-4 rounded-12">
                    <div className="flex items-center space-x-3 mb-2">
                      <Flame className="w-5 h-5 text-royal-gold flex-shrink-0" />
                      <h4 className="font-semibold text-off-white">{equipment.name}</h4>
                    </div>
                    <p className="text-gray-300 text-sm">{equipment.description}</p>
                  </div>
                ))}
              </div>

            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="space-y-6">
                <div className="glass-morphism p-8 rounded-24">
                  <h3 className="heading-font text-2xl font-semibold text-royal-gold mb-4">
                    Investigation Types
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {recoveryTypes.map((type, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-royal-gold flex-shrink-0" />
                        <span className="text-gray-200 text-sm">{type}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="relative rounded-24 overflow-hidden">
                  <img 
                    src="https://images.pexels.com/photos/16053029/pexels-photo-16053029.jpeg"
                    alt="Digital Forensics Lab"
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-near-black/60 to-transparent" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section> */}

    </div>
  );
};

export default DigitalForensicsPage;